
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App.tsx'
import './index.css'
import { ThemeProvider } from './hooks/useTheme.tsx'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Version and timestamp for cache busting
const APP_VERSION = window.APP_VERSION || "1.0.3"; // Increment version to force clean state
const BUILD_TIME = window.APP_BUILD_TIME || new Date().toISOString();
console.log(`App version: ${APP_VERSION} (Build: ${BUILD_TIME})`);

// Check for version changes and clear ALL browser storage if needed
const storedVersion = localStorage.getItem("app-version");
if (storedVersion !== APP_VERSION) {
  console.log(`Version changed from ${storedVersion || 'none'} to ${APP_VERSION}, clearing ALL stored data`);
  
  // Clear localStorage
  localStorage.clear();
  
  // Clear sessionStorage
  sessionStorage.clear();
  
  // Store new version
  localStorage.setItem("app-version", APP_VERSION);
  
  // Reset theme preference to system
  localStorage.setItem("app-theme", "system");
  
  // Clear IndexedDB databases (which Supabase might be using)
  if (window.indexedDB) {
    try {
      window.indexedDB.databases().then(databases => {
        if (databases && databases.length > 0) {
          console.log(`Clearing ${databases.length} IndexedDB databases`);
          databases.forEach(db => {
            if (db.name) {
              console.log(`Deleting IndexedDB database: ${db.name}`);
              try {
                window.indexedDB.deleteDatabase(db.name);
              } catch (dbError) {
                console.error(`Error deleting database ${db.name}:`, dbError);
              }
            }
          });
        } else {
          console.log('No IndexedDB databases to clear');
        }
      }).catch(error => {
        console.error('Error listing IndexedDB databases:', error);
      });
    } catch (idbError) {
      console.error('Error accessing IndexedDB:', idbError);
    }
  }
  
  // Clear any cookies by setting expired date
  // Note: This only works for cookies without HttpOnly flag
  document.cookie.split(";").forEach(cookie => {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    if (name && name !== "app-version") {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  });
}

// Import the centralized auth cleanup utility
import { clearAuthStorage, setupAuthErrorHandling } from './utils/authCleanup';

// Detect possible corruptions or errors
window.addEventListener('error', (event) => {
  // Check if the error is related to auth/storage
  const errorMsg = event.message || '';
  const isAuthError = errorMsg.toLowerCase().includes('auth') || 
                     errorMsg.toLowerCase().includes('token') ||
                     errorMsg.toLowerCase().includes('session');
                     
  if (isAuthError) {
    console.warn('Auth-related error detected, clearing auth storage:', errorMsg);
    clearAuthStorage();
  }
});

// Create a client with improved cache settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute (less aggressive caching)
      gcTime: 2 * 60 * 1000, // Keep unused data for 2 minutes (shorter)
      refetchOnWindowFocus: false, // Don't refetch on focus to reduce loops
      refetchOnMount: true, // Refetch on component mount
      retry: false, // Disable retries to prevent infinite loops
      retryOnMount: false, // Don't retry on mount
    },
  },
})

// Set up global auth error handling early
setupAuthErrorHandling();

// Create a helper function for namespaced storage access
window.getStorageKey = function(key) {
  return `pricepal-${key}-${APP_VERSION}`;
};

// Create a helper function for safe data retrieval
window.getSafeStorage = function(key, validate) {
  try {
    const namespaced = window.getStorageKey(key);
    const raw = localStorage.getItem(namespaced);
    
    if (!raw) return null;
    
    const parsed = JSON.parse(raw);
    
    // If validation function provided, use it
    if (validate && typeof validate === 'function') {
      return validate(parsed) ? parsed : null;
    }
    
    return parsed;
  } catch (e) {
    console.error(`Error retrieving ${key} from storage:`, e);
    // Clear corrupted data
    try {
      localStorage.removeItem(window.getStorageKey(key));
    } catch (clearError) {
      console.error(`Error clearing corrupted data for ${key}:`, clearError);
    }
    return null;
  }
};

// Create a helper function for safe data storage
window.setSafeStorage = function(key, data) {
  try {
    const namespaced = window.getStorageKey(key);
    localStorage.setItem(namespaced, JSON.stringify(data));
    return true;
  } catch (e) {
    console.error(`Error storing ${key} in storage:`, e);
    return false;
  }
};

// Create the root and render the app with proper BrowserRouter wrapping
const root = createRoot(document.getElementById("root")!);

// Render the app
root.render(
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="app-theme">
        <App />
      </ThemeProvider>
    </QueryClientProvider>
  </BrowserRouter>
);
